import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as Body from '../Styles'
import { useVarList } from './hooks/useVarList'
import { useChartData, isMultiPanelChart, chartRootId } from './hooks/useChartData'
import { useSugestoes, Sugestao } from './hooks/useSugestoes'
import { CHART_SIZES, ChartSizeW } from './ChartPanel'
import { LayoutItem, defaultItem } from './ChartGrid'
import Sidebar from './Sidebar'
import ChartGrid from './ChartGrid'
import homeIcon from '../../../assets/icons/home-icon.svg'
import * as Btn from '../../../components/Button/Styles'
import { registerLocale, setDefaultLocale } from 'react-datepicker'
import ptBR from 'date-fns/locale/pt-BR'
import { Square, Columns2, Grid3x2, LayoutGrid, ArrowUpDown, Plus, X, StepBack, ChevronLeft, Trash2 } from 'lucide-react'
import { useIsMobile } from '../../../utils/hooks/useIsMobile'
import { useBodyScrollLock } from '../../../utils/hooks/useBodyScrollLock'

registerLocale('pt-BR', ptBR)
setDefaultLocale('pt-BR')

// ── Configuração de tamanhos ──────────────────────────────────────

const SIZES: { w: ChartSizeW; label: string; Icon: React.FC<{ size: number; color: string }> }[] = [
    { w: 12, label: 'Stretch', Icon: ({ size, color }) => <Square    size={size} color={color} strokeWidth={1.8} /> },
    { w: 6,  label: 'Duo',     Icon: ({ size, color }) => <Columns2  size={size} color={color} strokeWidth={1.8} /> },
    { w: 4,  label: 'Trio',    Icon: ({ size, color }) => <Grid3x2   size={size} color={color} strokeWidth={1.8} /> },
    { w: 3,  label: 'Quadra',  Icon: ({ size, color }) => <LayoutGrid size={size} color={color} strokeWidth={1.8} /> },
]

// Chave de ordenação: [número da entidade (terno/bomba), sub-tipo]
function getSortKey(id: string): [number, number] {
    const terno = id.match(/:t(\d+)/)
    const bomba = id.match(/bomba_(\d+)/)
    const entity = terno ? parseInt(terno[1]) : bomba ? parseInt(bomba[1]) : 999
    const type   = id.includes('pressao') ? 0 : id.includes('deslocamento') ? 1 : 0
    return [entity, type]
}

function reflowOrdered(items: LayoutItem[]): LayoutItem[] {
    const COLS = 12; let x = 0, y = 0, rowH = 0
    return items.map(item => {
        const { w, h } = item
        if (x + w > COLS) { y += rowH; x = 0; rowH = 0 }
        const placed = { ...item, x, y }
        x += w; rowH = Math.max(rowH, h)
        return placed
    })
}

function reflow(items: LayoutItem[], targetW: number): LayoutItem[] {
    const perRow = Math.max(1, Math.floor(12 / targetW))
    // preserva ordem visual aproximada
    const sorted = [...items].sort((a, b) => a.y !== b.y ? a.y - b.y : a.x - b.x)
    return sorted.map((item, i) => ({
        ...item,
        w: targetW,
        x: (i % perRow) * targetW,
        y: Math.floor(i / perRow) * item.h,
    }))
}

// ── Componente ────────────────────────────────────────────────────

const Charts = () => {
    const navigate = useNavigate()
    const [params] = useSearchParams()
    const token = params.get('token') ?? ''
    const nomeUsina = params.get('nome-usina') ?? ''
    // Ternos realmente cadastrados na usina (vindos de Moenda.tsx, que já filtra via /moenda) —
    // /graficos/variaveis não filtra, então sem isso a Sidebar ofereceria "Terno N" para ternos
    // sem o 4.0 instalado. Sem o param (ex: acesso direto à URL), cai no fallback por contagem.
    const ternosParam = params.get('ternos') ?? ''
    const activeTernos = ternosParam
        ? ternosParam.split(',').map(Number).filter(n => !isNaN(n) && n > 0)
        : undefined
    const isMobile = useIsMobile()
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [periodoPendente, setPeriodoPendente] = useState(false)
    useBodyScrollLock(sidebarOpen)

    const now = new Date()
    const [inicio, setInicio] = useState(new Date(now.getTime() - 30 * 60 * 1000))
    const [fim,    setFim]    = useState(now)
    const [activeSize, setActiveSize] = useState<ChartSizeW>(6)

    // ── Estado de estatísticas ───────────────────────────────────
    const [enabledStats, setEnabledStats] = useState<Set<string>>(new Set())
    const handleToggleStat = (key: string) => {
        setEnabledStats(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    // ── Estado de escala do eixo Y ───────────────────────────────
    type AxisMode = 'amplo' | 'moderado' | 'preciso' | 'manual'
    const [axisMode, setAxisMode] = useState<AxisMode>('moderado')
    const [axisConfig, setAxisConfig] = useState<Record<string, { min: string; max: string }>>({})

    const handleAxisConfigChange = (chartId: string, field: 'min' | 'max', value: string) => {
        setAxisConfig(prev => ({
            ...prev,
            [chartId]: { ...(prev[chartId] ?? { min: '', max: '' }), [field]: value }
        }))
    }

    const PAD: Record<AxisMode, number> = { amplo: 0.30, moderado: 0.10, preciso: 0.02, manual: 0 }

    /** Retorna o domain do YAxis para um chart específico */
    const getAxisDomain = (chartId: string): ['auto' | number, 'auto' | number] => {
        if (axisMode === 'manual') {
            const cfg = axisConfig[chartId]
            const min = cfg?.min !== '' && cfg?.min !== undefined ? Number(cfg.min) : 'auto'
            const max = cfg?.max !== '' && cfg?.max !== undefined ? Number(cfg.max) : 'auto'
            return [min, max]
        }

        const chart = charts.find(c => c.id === chartId)
        if (!chart) return ['auto', 'auto']

        const allValues: number[] = chart.series?.length
            ? chart.series.flatMap(s => s.points.map(p => Number(p.value))).filter(isFinite)
            : chart.points.map(p => Number(p.value)).filter(isFinite)

        if (allValues.length === 0) return ['auto', 'auto']

        const dataMin = Math.min(...allValues)
        const dataMax = Math.max(...allValues)
        const range   = dataMax - dataMin

        // Linha reta (um único valor) — domain 'auto' cola a linha no eixo inferior em vez de
        // centralizar, então cria um respiro simétrico artificial em torno do valor.
        if (range === 0) {
            const v = dataMin
            const pad = Math.max(Math.abs(v), 1) * Math.max(PAD[axisMode], 0.05)
            return [
                parseFloat((v - pad).toFixed(4)),
                parseFloat((v + pad).toFixed(4)),
            ]
        }

        const pad = PAD[axisMode] * range
        return [
            parseFloat((dataMin - pad).toFixed(4)),
            parseFloat((dataMax + pad).toFixed(4)),
        ]
    }

    const { categorias } = useVarList(token)
    const { charts, fetchChart, removeChart, removeChartsByGrafico, clearCharts } = useChartData(token)
    const { sugestoes } = useSugestoes(token)

    // ── Layout state (lifted para cá) ────────────────────────────
    const [layout, setLayoutState] = useState<LayoutItem[]>([])

    const setLayout = (l: LayoutItem[]) => {
        setLayoutState(l)
        localStorage.setItem(`chart-layout-${token}`, JSON.stringify(l))
    }

    // ── Resize all ───────────────────────────────────────────────
    const resizeAll = (w: ChartSizeW) => {
        setActiveSize(w)
        setLayoutState(prev => {
            const existing = prev.length > 0 ? prev : charts.map((c, i) => defaultItem(c.id, i))
            return reflow(existing, w)
        })
    }

    // ── Resize one ───────────────────────────────────────────────
    const resizeOne = (id: string, w: number) => {
        setLayoutState(prev => prev.map(item => item.i === id ? { ...item, w } : item))
    }

    // ── Adicionar gráfico ─────────────────────────────────────────
    // Buscas "+ Todos" (multi-painel, sem onlyId) em andamento — usado só pra desabilitar o botão.
    const [pendingAddAll, setPendingAddAll] = useState<Set<string>>(new Set())

    /**
     * @param onlyId  Sub-item: terno number '1','2'… ou bomba 'bomba_1'…
     *                Omitir = adiciona todos.
     */
    const handleAddChart = async (grafico: string, label: string, campo?: string, onlyId?: string) => {
        const isVar = !!campo
        const baseId = isVar ? `variavel:${campo}` : grafico

        if (onlyId) {
            let subPanelId: string
            if (grafico === 'ternos')    subPanelId = `ternos:t${onlyId}:pressao`
            // onlyId vem como 'bomba_N' — id real do painel é 'teste_bomba_N' (ver useChartData)
            else if (grafico === 'autoteste') subPanelId = `autoteste:teste_${onlyId}`
            else                          subPanelId = `${grafico}:t${onlyId}`
            if (charts.some(c => c.id === subPanelId)) return
        } else {
            // Sem onlyId: verifica se algum sub-painel já existe, ou se a busca "+ Todos" já está
            // em andamento. Também cruza namespaces predefinido ↔ variável personalizada.
            const alreadyAdded = isMultiPanelChart(grafico)
                ? charts.some(c => c.id.startsWith(baseId + ':')) || pendingAddAll.has(grafico)
                : charts.some(c => c.id === baseId)
                    || (!isVar && charts.some(c => c.id === `variavel:${grafico}`))  // predefinido vs var com mesmo campo
                    || ( isVar && charts.some(c => c.id === campo))                   // var personalizada vs predefinido com mesmo ID
            if (alreadyAdded) return
        }

        const isPendingAllFetch = !onlyId && isMultiPanelChart(grafico)
        if (isPendingAllFetch) setPendingAddAll(prev => new Set(prev).add(grafico))
        try {
            await fetchChart(grafico, label, inicio, fim, campo, onlyId)
        } finally {
            if (isPendingAllFetch) {
                setPendingAddAll(prev => { const next = new Set(prev); next.delete(grafico); return next })
            }
        }
    }

    // ── Remover gráfico pela sidebar ─────────────────────────────
    const handleRemoveChart = (grafico: string, campo?: string, onlyId?: string) => {
        removeChartsByGrafico(grafico, campo, onlyId)
    }

    // ── Limpar todos os gráficos ──────────────────────────────────
    const handleClearCharts = () => {
        clearCharts()
        setLayout([])
    }

    // ── Selecionar gráfico sugerido ───────────────────────────────
    const handleSelectSugestao = (s: Sugestao) => {
        handleAddChart(s.grafico, s.titulo, s.campo ?? undefined, s.onlyId ?? undefined)
    }

    // ── Reorganizar por terno/bomba ───────────────────────────────
    const reorganize = () => {
        setLayoutState(prev => {
            const full = charts.map((c, i) => prev.find(l => l.i === c.id) ?? defaultItem(c.id, i))
            const sorted = [...full].sort((a, b) => {
                const [eA, tA] = getSortKey(a.i), [eB, tB] = getSortKey(b.i)
                return eA - eB || tA - tB
            })
            return reflowOrdered(sorted)
        })
    }

    // ── Trocar período ───────────────────────────────────────────
    /**
     * Re-busca APENAS os sub-painéis que estão visíveis no momento.
     * Bug anterior: usava chartRootId → chamava fetchChart('ternos') sem onlyId
     * → adicionava todos os ternos, mesmo que só um estivesse selecionado.
     */
    const handleDateChange = (newInicio: Date, newFim: Date) => {
        setInicio(newInicio)
        setFim(newFim)
        const seen = new Set<string>()

        charts.forEach(c => {
            if (seen.has(c.id)) return

            // 1. Variável personalizada
            if (c.id.startsWith('variavel:')) {
                seen.add(c.id)
                fetchChart('variavel', c.label, newInicio, newFim, c.id.replace('variavel:', ''))
                return
            }

            // 2. Ternos: ternos:t1:pressao e ternos:t1:deslocamento → uma fetch por terno
            const ternoMatch = c.id.match(/^ternos:t(\d+):/)
            if (ternoMatch) {
                const key = `ternos:t${ternoMatch[1]}`
                if (!seen.has(key)) {
                    seen.add(key)
                    fetchChart('ternos', c.label, newInicio, newFim, undefined, ternoMatch[1])
                }
                seen.add(c.id) // marca o painel individual também
                return
            }

            // 3. Autoteste: autoteste:teste_bomba_1 → onlyId = 'teste_bomba_1'
            const autoMatch = c.id.match(/^autoteste:(.+)$/)
            if (autoMatch) {
                seen.add(c.id)
                fetchChart('autoteste', c.label, newInicio, newFim, undefined, autoMatch[1])
                return
            }

            // 4. Per-terno (media-status-terno:t1, terno-critico:t2, tempo-nivelamento:t1)
            const perTernoMatch = c.id.match(/^(.+):t(\d+)$/)
            if (perTernoMatch) {
                seen.add(c.id)
                fetchChart(perTernoMatch[1], c.label, newInicio, newFim, undefined, perTernoMatch[2])
                return
            }

            // 5. Gráfico simples (temperatura-oleo, pressao-filtro, etc.)
            seen.add(c.id)
            fetchChart(c.id, c.label, newInicio, newFim)
        })
    }

    // ── Render ───────────────────────────────────────────────────
    const sidebarProps = {
        token,
        categorias,
        activeTernos,
        inicio,
        fim,
        onDateChange: handleDateChange,
        onPeriodoPendingChange: setPeriodoPendente,
        isMobile,
        onReorganize: isMobile ? reorganize : undefined,
        onClearCharts: isMobile ? handleClearCharts : undefined,
        sugestoes,
        onSelectSugestao: (s: Sugestao) => {
            handleSelectSugestao(s)
            if (isMobile) setSidebarOpen(false)
        },
        onAddChart: (grafico: string, label: string, campo?: string, onlyId?: string) => {
            handleAddChart(grafico, label, campo, onlyId)
            if (isMobile) setSidebarOpen(false)
        },
        onRemoveChart: handleRemoveChart,
        existingChartIds: charts.map(c => c.id),
        loadingChartIds: charts.filter(c => c.loading).map(c => c.id),
        pendingAddAllIds: Array.from(pendingAddAll),
        chartLabels: Object.fromEntries(charts.map(c => [c.id, c.label])),
        enabledStats,
        onToggleStat: handleToggleStat,
        axisMode,
        onAxisModeChange: setAxisMode,
        axisConfig,
        onAxisConfigChange: handleAxisConfigChange,
    }

    return (
        <Body.Container>
            <Body.Nav>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <Body.NavTitle style={{textTransform: "uppercase"}}>Gráficos</Body.NavTitle>
                    {!isMobile && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
                            {/* Pill de tamanhos */}
                            <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: 3, gap: 2 }}>
                                {SIZES.map(s => {
                                    const active = activeSize === s.w
                                    const color = active ? '#fff' : 'rgba(255,255,255,0.4)'
                                    return (
                                        <button key={s.w} title={s.label} onClick={() => resizeAll(s.w)}
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 30, borderRadius: 9, border: 'none', background: active ? 'rgba(255,255,255,0.15)' : 'transparent', cursor: 'pointer', transition: 'background 0.15s', padding: 0 }}
                                        >
                                            <s.Icon size={16} color={color} />
                                        </button>
                                    )
                                })}
                            </div>
                            {/* Botão reorganizar */}
                            <button title="Reorganizar por terno/bomba" onClick={reorganize}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 10, border: 'none', background: 'rgba(255,255,255,0.07)', cursor: 'pointer', transition: 'background 0.15s' }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.13)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.07)')}
                            >
                                <ArrowUpDown size={15} color="rgba(255,255,255,0.5)" strokeWidth={1.8} />
                            </button>
                            {/* Botão limpar gráficos */}
                            <button title="Limpar gráficos" disabled={charts.length === 0} onClick={handleClearCharts}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34,
                                    borderRadius: 10, border: 'none', background: 'rgba(255,255,255,0.07)',
                                    cursor: charts.length === 0 ? 'default' : 'pointer', opacity: charts.length === 0 ? 0.4 : 1,
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => { if (charts.length > 0) e.currentTarget.style.background = 'rgba(240,63,63,0.15)' }}
                                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.07)')}
                            >
                                <Trash2 size={15} color="rgba(255,255,255,0.5)" strokeWidth={1.8} />
                            </button>
                        </div>
                    )}
                </div>
                <Body.NavButtons style={{ alignItems: 'center', gap: 10 }}>
                    {nomeUsina && <Body.NavSubtitle style={{ fontSize: 16, whiteSpace: 'nowrap' }}>{nomeUsina}</Body.NavSubtitle>}
                    <Btn.Button onClick={() => navigate(-1)}>
                        {/* <Body.BtnIcon src={homeIcon} /> */}
                        <ChevronLeft/>
                    </Btn.Button>
                </Body.NavButtons>
            </Body.Nav>

            <div style={{ display: 'flex', flex: 1, minHeight: 0, width: '100%', gap: 12, paddingBottom: 12, boxSizing: 'border-box' }}>
                {!isMobile && (
                    /* Sidebar — 25%, painel lateral fixo (desktop) */
                    <div style={{ width: '25%', minWidth: 240, maxWidth: 320, flexShrink: 0 }}>
                        <Sidebar {...sidebarProps} />
                    </div>
                )}

                {/* Chart grid */}
                <div style={{ flex: 1, minWidth: 0, overflowY: 'auto'}}>
                    <ChartGrid
                        charts={charts}
                        layout={layout}
                        setLayout={setLayout}
                        onRemove={removeChart}
                        onResizeOne={resizeOne}
                        enabledStats={enabledStats}
                        getAxisDomain={getAxisDomain}
                        isMobile={isMobile}
                        periodoPendente={periodoPendente}
                    />
                </div>
            </div>

            {isMobile && (
                <>
                    <button onClick={() => setSidebarOpen(true)}
                        style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 200, width: 52, height: 52, borderRadius: '50%', border: 'none', background: 'linear-gradient(90deg, #92B258, #5292D1)', boxShadow: '0 6px 16px rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        title="Adicionar gráfico"
                    >
                        <Plus size={24} color="#fff" />
                    </button>
                    {sidebarOpen && (
                        <div style={{ position: 'fixed', inset: 0, zIndex: 400, background: '#101720', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, position: 'sticky', top: 0, background: '#101720', zIndex: 1, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                                <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 16, color: '#fff' }}>Gráficos & Filtros</span>
                                <button onClick={() => setSidebarOpen(false)}
                                    style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: 'rgba(255,255,255,0.6)', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <X size={16} />
                                </button>
                            </div>
                            <div style={{ padding: 16 }}>
                                <Sidebar {...sidebarProps} />
                            </div>
                        </div>
                    )}
                </>
            )}
        </Body.Container>
    )
}

export default Charts
